import { useState, useEffect } from "react";
import { API, useAuth, authFetch } from "@/App";
import { toast } from "sonner";
import { AlertTriangle, Plus, MessageSquare, CheckCircle, Clock, XCircle } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
const STATUS_COLORS = {
  OPEN: "bg-red-100 text-red-700",
  IN_PROGRESS: "bg-amber-100 text-amber-700",
  RESOLVED: "bg-emerald-100 text-emerald-700",
  CLOSED: "bg-slate-100 text-slate-600",
};

const GrievancePage = () => {
  const { user } = useAuth();
  const [grievances, setGrievances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedGrievance, setSelectedGrievance] = useState(null);
  const [formData, setFormData] = useState({
    grievance_type: "EMPLOYEE", subject: "", description: "", priority: "MEDIUM",
  });

  const fetchGrievances = async () => {
    try {
      let url = `${API}/grievances?limit=50`;
      if (typeFilter !== "all") url += `&grievance_type=${typeFilter}`;
      if (statusFilter !== "all") url += `&status=${statusFilter}`;
      const res = await authFetch(url);
      const data = await res.json();
      if (data.success) setGrievances(data.data);
    } catch { console.error("Failed to load grievances"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchGrievances(); }, [typeFilter, statusFilter]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const res = await authFetch(`${API}/grievances`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...formData, submitted_by: user?.user_id, submitted_by_name: user?.name }),
      });
      const data = await res.json();
      if (data.success) {
        toast.success("Grievance submitted");
        setIsDialogOpen(false);
        setFormData({ grievance_type: "EMPLOYEE", subject: "", description: "", priority: "MEDIUM" });
        fetchGrievances();
      }
    } catch { toast.error("Failed to submit"); }
  };

  const updateStatus = async (id, status, resolution) => {
    try {
      await authFetch(`${API}/grievances/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status, resolution: resolution || "" }),
      });
      toast.success("Status updated");
      fetchGrievances();
      setSelectedGrievance(null);
    } catch { toast.error("Failed to update"); }
  };

  const stats = {
    open: grievances.filter(g => g.status === "OPEN").length,
    in_progress: grievances.filter(g => g.status === "IN_PROGRESS").length,
    resolved: grievances.filter(g => g.status === "RESOLVED").length,
  };

  return (
    <div className="space-y-6" data-testid="grievance-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Sora']">Grievance Redressal</h1>
          <p className="text-slate-500 text-sm mt-1">Employee & Dealer complaints management</p>
        </div>
        <Button onClick={() => setIsDialogOpen(true)} className="bg-[#8B1A1A] hover:bg-[#6B1515] text-white" data-testid="new-grievance-btn">
          <Plus className="w-4 h-4 mr-2" /> New Grievance
        </Button>
      </div>

      {/* Pending Approvals Alert */}
      {user?.role === "SUPER_ADMIN" && stats.open > 0 && (
        <Card className="p-4 border-amber-200 bg-amber-50" data-testid="pending-grievances-alert">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            <div className="flex-1">
              <p className="font-medium text-amber-800">{stats.open} grievance{stats.open > 1 ? "s" : ""} needs attention</p>
              <p className="text-sm text-amber-600">Open complaints awaiting review and resolution.</p>
            </div>
            <Button variant="outline" size="sm" className="border-amber-300 text-amber-700 hover:bg-amber-100"
              onClick={() => setStatusFilter("OPEN")} data-testid="review-pending-grievances">Review Now</Button>
          </div>
        </Card>
      )}

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: "Open", value: stats.open, icon: AlertTriangle, color: "text-red-600", bg: "bg-red-50" },
          { label: "In Progress", value: stats.in_progress, icon: Clock, color: "text-amber-600", bg: "bg-amber-50" },
          { label: "Resolved", value: stats.resolved, icon: CheckCircle, color: "text-emerald-600", bg: "bg-emerald-50" },
        ].map((s, i) => (
          <Card key={i} className="p-4 flex items-center gap-3">
            <div className={`p-2 rounded-lg ${s.bg}`}><s.icon className={`w-5 h-5 ${s.color}`} /></div>
            <div>
              <p className="text-2xl font-bold font-mono text-slate-900">{s.value}</p>
              <p className="text-xs text-slate-500">{s.label}</p>
            </div>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <div className="flex gap-3">
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-40" data-testid="type-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="EMPLOYEE">Employee</SelectItem>
            <SelectItem value="DEALER">Dealer</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40" data-testid="status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="OPEN">Open</SelectItem>
            <SelectItem value="IN_PROGRESS">In Progress</SelectItem>
            <SelectItem value="RESOLVED">Resolved</SelectItem>
            <SelectItem value="CLOSED">Closed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Grievance List */}
      {loading ? (
        <div className="flex justify-center py-12"><div className="w-8 h-8 spinner" /></div>
      ) : grievances.length === 0 ? (
        <Card className="p-8 text-center">
          <MessageSquare className="w-12 h-12 mx-auto mb-3 text-slate-300" />
          <p className="text-lg font-medium text-slate-500">No grievances found</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {grievances.map((g) => (
            <Card key={g.grievance_id} className="p-4 hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => setSelectedGrievance(g)} data-testid={`grievance-${g.grievance_id}`}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge className={`text-xs ${STATUS_COLORS[g.status]}`}>{g.status}</Badge>
                    <Badge variant="outline" className="text-xs">{g.grievance_type}</Badge>
                    <Badge variant="outline" className={`text-xs ${g.priority === "HIGH" ? "border-red-300 text-red-600" : g.priority === "LOW" ? "border-slate-300" : "border-amber-300 text-amber-600"}`}>{g.priority}</Badge>
                  </div>
                  <h3 className="font-semibold text-slate-900">{g.subject}</h3>
                  <p className="text-sm text-slate-500 mt-1 line-clamp-2">{g.description}</p>
                  <p className="text-xs text-slate-400 mt-2">By {g.submitted_by_name} &middot; {new Date(g.created_at).toLocaleDateString()}</p>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* New Grievance Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-md" data-testid="grievance-dialog">
          <DialogHeader><DialogTitle>Submit Grievance</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-slate-700">Type</label>
                <Select value={formData.grievance_type} onValueChange={(v) => setFormData({ ...formData, grievance_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="EMPLOYEE">Employee</SelectItem>
                    <SelectItem value="DEALER">Dealer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Priority</label>
                <Select value={formData.priority} onValueChange={(v) => setFormData({ ...formData, priority: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="LOW">Low</SelectItem>
                    <SelectItem value="MEDIUM">Medium</SelectItem>
                    <SelectItem value="HIGH">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Subject</label>
              <Input value={formData.subject} onChange={(e) => setFormData({ ...formData, subject: e.target.value })} required data-testid="grievance-subject" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Description</label>
              <Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={4} required />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-[#8B1A1A] hover:bg-[#6B1515] text-white">Submit</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={!!selectedGrievance} onOpenChange={() => setSelectedGrievance(null)}>
        <DialogContent className="max-w-md" data-testid="grievance-detail">
          {selectedGrievance && (
            <>
              <DialogHeader><DialogTitle>{selectedGrievance.subject}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div className="flex gap-2">
                  <Badge className={STATUS_COLORS[selectedGrievance.status]}>{selectedGrievance.status}</Badge>
                  <Badge variant="outline">{selectedGrievance.grievance_type}</Badge>
                  <Badge variant="outline">{selectedGrievance.priority}</Badge>
                </div>
                <p className="text-sm text-slate-600">{selectedGrievance.description}</p>
                <p className="text-xs text-slate-400">Submitted by {selectedGrievance.submitted_by_name} on {new Date(selectedGrievance.created_at).toLocaleString()}</p>
                {selectedGrievance.resolution && (
                  <div className="bg-emerald-50 p-3 rounded-lg">
                    <p className="text-xs font-medium text-emerald-700">Resolution</p>
                    <p className="text-sm text-emerald-800">{selectedGrievance.resolution}</p>
                  </div>
                )}
                {user?.role === "SUPER_ADMIN" && selectedGrievance.status !== "CLOSED" && (
                  <div className="flex gap-2 pt-2 border-t">
                    {selectedGrievance.status === "OPEN" && (
                      <Button size="sm" variant="outline" onClick={() => updateStatus(selectedGrievance.grievance_id, "IN_PROGRESS")}>Mark In Progress</Button>
                    )}
                    <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      onClick={() => { const r = prompt("Resolution:"); if (r) updateStatus(selectedGrievance.grievance_id, "RESOLVED", r); }}>Resolve</Button>
                    <Button size="sm" variant="outline" onClick={() => updateStatus(selectedGrievance.grievance_id, "CLOSED")}>Close</Button>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GrievancePage;
